import type { ConversationArchiveState, BrainConversationMembership, BrainSessionSnapshot, VoiceConfig, BrainSessionIdentity, BrainConversationIcon, ProjectExecutionRef, ModelOverrides, BrainCard, BrainChatRailSection, ConversationLiveUsage, ConversationProjectIdentity, SkillReviewStatusView, Profile } from '../../src/shared/wireContract';
export type { UserPatch, UserSpendSummary, VoiceConfig, UserVoiceSettings, UserVoiceSettingsSnapshot, UserVoiceSettingsPatch, VoiceTranscriptEntry, VoiceCallRecord, VoiceRingPayload, VoiceRingEndReason, VoiceCallState, VoiceCallEndReason, VoiceBrowserControl, VoiceCallRequest, VoiceCallResponse, VoiceRingResponse, VoiceRingDeclineResponse, VoiceCatalog } from '../../src/shared/wireContract';
import type { KeyAccountKind } from '../../src/shared/keyAccounts';
import type { BrainProviderType, BrainRouterConfig } from './providerAccounts';
export type { KeyAccountKind, KeyAccountView } from '../../src/shared/keyAccounts';
import type { BrainProviderConnectionView, ProviderRoutingConfig } from '../../src/shared/providerAccounts';
export type { BrainProviderConnectionView, ProviderRoutingConfig } from '../../src/shared/providerAccounts';
import type { PluginCredentialsSummary } from '../../src/shared/wireContract';
export type { PluginCredentialsSummary };
export type {
  BrainRecalledInput, BrainConversationNotificationsUpdate, BrainTypingRequest, BrainTypingParticipant,
  BrainSessionSnapshot, Profile, BrainConversationMembership, BrainAgentPauseResponse, BrainParticipantsResponse,
  BrainInvitableResponse, BrainQueueMemberItem, SkillReviewStatusView, UserDirectoryRow,
  ConversationArchiveState, BrainConversationArchiveRequest, BrainConversationArchiveResponse,
} from '../../src/shared/wireContract';
// The browser's reaction vocabulary is the pinned mirror of src/shared/reactions.ts, not a second copy of it.
export type { ReactionEmoji } from './reactions';
import type { PluginConfigField } from '../../src/plugins/manifest';
import type { PluginDependency } from '../../src/plugins/controlDependencies';

export type PromptSuggestionModel = 'same-model-with-cache' | { providerId: string; model: string };

export interface ElowenConfig {
  /** Absent or an empty pair inherits the utility model. */
  promptSuggestionModel?: PromptSuggestionModel;
  allowedSkins: string[];
  security: { tokenTtlDays: number; trustProxy: boolean };
  sessionRetention: { enabled: boolean; days: number };
  autoUpdate: boolean;
  problemReporting: { enabled: boolean };
  webPushContact?: string;
  plugins?: { enabled: string[]; removed?: string[] };
  brain?: { providers: BrainProvider[]; defaultModel?: { providerId: string; model: string } | null; maxSteps?: number; modelOverrides?: ModelOverrides; limits?: BrainLimits; forkParentContext?: boolean };
  /** Operator-tunable runtime knobs — the sibling group of `brain.limits`; absent on an older daemon. */
  runtime?: RuntimeConfig;
  /** Dashboard personalization (recap strip + agent-written hero); absent on an older daemon. */
  dashboard?: DashboardConfig;
  voice?: VoiceConfig;
}

export interface ConfigSnapshot extends ElowenConfig { revision: number }

/** Mirror of the daemon's dashboard config block. */
interface DashboardConfig {
  recapEnabled: boolean;
  digestEnabled: boolean;
  greetingEnabled: boolean;
  pillsEnabled: boolean;
  continueEnabled: boolean;
  /** How many times a day the digest may regenerate; 1 keeps it to a single run per user per day. */
  digestPerDay: number;
  /** How many recap variants ONE generation writes, 1–10; absent on a daemon older than the batch. */
  digestVariants?: number;
  digest: { providerId: string; model: string };
}

/** One agent-written clickable action (a quick-action pill or a recap suggestion). */
interface DashAction { label: string; prompt: string }

/** One complete personalized dashboard in the rotation. Earlier saved batches omit per-variant hero
 *  fields; those inherit the top-level greeting/question/pills without a paid regeneration. */
export interface DashRecapVariant {
  greeting?: string;
  ask?: string;
  pills?: DashAction[];
  summary?: string;
  suggestions?: DashAction[];
}

/** /dash/recap — the personalized dashboard surface, strictly per-caller. `GET` reads what is stored;
 *  `POST` answers the same and starts the day's digest when one is due. */
export interface DashRecap {
  enabled: boolean;
  /** `GET` only: a digest generation is due, so the client should refresh with `POST`. */
  refreshDue?: boolean;
  continue?: ({ id: string; title: string; updatedAt: string } & BrainConversationIcon)[];
  yesterday?: { turns: number; tokens: number; sessions: string[] } | null;
  digest?: {
    status: 'unavailable' | 'generating' | 'ready';
    greeting?: string;
    /** The agent-written standing question under the greeting; absent → the interface's own phrasing. */
    ask?: string;
    pills?: DashAction[];
    summary?: string;
    suggestions?: DashAction[];
    /** The variant batch to rotate between client-side; variant 1 is always summary/suggestions. */
    recaps?: DashRecapVariant[];
  };
}

// Operator-tunable brain limits — shared with the daemon via the wire contract (re-exported from it
// further down this file); every field is a whole number the daemon clamps to a sane range.


export interface BrainNativeProvider {
  id: string;
  label: string;
  type: Exclude<BrainProviderType, 'router'>;
  baseUrl: string;
  models: string[];
  /** Wire API pin for `openai` entries. Absent selects and remembers support per model. */
  api?: 'openai-completions' | 'openai-responses';
  /** Explicit Chat Completions capabilities. Present on every OpenAI-compatible provider returned by the daemon. */
  compatibility?: BrainProviderCompatibility;
  /** The provider's accounts, secret-free: each carries only whether a key is configured. An account's
   *  `enabled` is the operator's pause switch and `access` its user rule. */
  connections: BrainProviderConnectionView[];
  /** The provider's typed routing policy (see `providerAccounts.ts`). */
  routing: ProviderRoutingConfig;
  /** Sampling temperature. Absent = the field is not sent and the model's own default applies. */
  temperature?: number;
  /** Present only when the operator switched this provider's NATIVE (hosted) tool search off. The literal
   *  type mirrors the daemon: "on" is the ABSENCE of the field, never a `true`, so nothing a client sends
   *  can grant a route the provider gates have not already earned. */
  hostedToolSearchEnabled?: false;
  /** Present only when the operator switched code mode ON for this provider. The literal mirrors the
   *  daemon the other way round from the field above: "off" is the ABSENCE of the field. The switch alone
   *  grants nothing — the provider wire and the model family still have to pass. */
  codeModeEnabled?: true;
  responsesStatefulEnabled?: true;
  /** Set when this entry is a key account's connection (`GET /brain/key-accounts` lists the kinds). The Accounts
   *  group owns such an entry; the custom-provider list never shows it. */
  account?: KeyAccountKind;
}
export type BrainProvider = BrainNativeProvider | BrainRouterConfig;
export type { BrainModelCatalogOption as BrainModelOption } from '../../src/shared/wireContract';

/** Process-local automatic probe outcome from the admin wire status route. */
export interface WireApiProbeStatus {
  status: 'detected' | 'deferred' | 'error' | 'skipped';
  api?: string;
  reason?: string;
  httpStatus?: number;
  checkedAt: number;
}
export interface WireApiModelStatus {
  modelId: string;
  effective: { api: string; source: 'model-pin' | 'provider-pin' | 'catalog' | 'detected' | 'default' };
  detection?: Pick<import('../../src/shared/wireContract').WireApiDetection, 'api' | 'unsupported' | 'checkedAt' | 'compatibility'>;
  choices: import('../../src/shared/wireContract').WireApiKind[];
  probe?: WireApiProbeStatus;
}
export interface WireApiStatus {
  providers: { providerId: string; models: WireApiModelStatus[] }[];
}

/** One brain conversation in the session picker (web chat + CLI). `automation` says WHAT ran the turn
 *  this projection describes — 'scheduled' for a cron job or a wake-up, null for a turn a person asked
 *  for — and it outlives settlement, so a finished schedule and an answered question do not wear the
 *  same mark. `at` is the transition time, so a `working` run started then; `durationMs` is a settled run's
 *  work duration (the chat's own measure), filled only on settled rows that still need attention. */
export type BrainActivityView = import('../../src/shared/wireContract').BrainConversationActivity;
/** Current indicators come from the daemon's shared control projection. Durable activity remains the
 *  authority for unread results and scheduled completion. */
export interface BrainSessionInfo extends ConversationArchiveState, BrainConversationMembership, BrainConversationIcon, Pick<BrainStreamControl, 'programStatus' | 'backgroundWorking' | 'canStop'> { ownerSessionSendEligible: boolean; projectRef: ProjectExecutionRef | null; projectIdentity?: ConversationProjectIdentity; platform: 'api' | 'subagent' | 'web' | 'cli'; id: string; title: string; pinnedAt: string | null; provider?: string; model: string; updated_at: string; running: boolean; active: boolean; attached?: number; tokens?: number; cost?: number; activity?: BrainActivityView; liveUsage?: ConversationLiveUsage }
/** A row in the admin session-management panel (all brain sessions the operator anchors). `platform` says
 *  WHERE it happened (null for web/CLI) and `direct` whether `ownerId` is the person talking there or only
 *  the account hosting a shared room — see ManagedSessionView in src/brain/service/statusService.ts.
 *  `parentSessionId` is the conversation that DELEGATED this one, so the register can nest a sub-agent
 *  under its actual parent. It may name a row that is not in the same response (deleting a parent detaches
 *  its children instead of cascading): such a session is rendered as a root. Optional so a payload from an
 *  older daemon still types; absent reads exactly like a root. */
export interface ManagedSession extends ConversationArchiveState, BrainConversationMembership, BrainConversationIcon, Pick<BrainStreamControl, 'programStatus' | 'backgroundWorking' | 'canStop'> { projectRef: ProjectExecutionRef | null; activity?: Omit<BrainActivityView, 'unread' | 'durationMs'>; id: string; title: string; provider?: string; model: string; updated_at: string; running: boolean; active: boolean; kind: 'conversation' | 'channel'; tokens: number; cost?: number; platform: string | null; direct: boolean; ownerId: number; ownerLabel: string; parentSessionId?: string | null; lastWriterId: number | null; lastWriterLabel: string | null; liveUsage?: ConversationLiveUsage }
/** Explicit instance scope is administrator-only; omission retains caller-only search. */
export type BrainSearchScope = 'mine' | 'instance';
/** One bounded fulltext-search match in the explicitly authorized scope. */
export interface BrainSearchHit { sessionId: string; sessionTitle: string; role: string; snippet: string; ts: string }

/** Site-wide search assistance (POST /search/rank, POST /search/ask) — the two layers behind the command
 *  palette's own lexical filter. The palette sends its OWN index as candidates and gets ids back, so the
 *  daemon never learns a route: it ranks, or it picks, and the palette resolves the ids against the rows
 *  it already built. `score` is cosine similarity; an ask answer carries no score because there is none. */
export interface SearchRankCandidate { id: string; text: string }
export interface SearchRankResult { results: { id: string; score: number }[] }
export type { SearchAskCandidate, SearchAskResult } from '../../src/shared/siteSearch';
/** The display-transcript shapes AND the REST DTOs the dock lists are the daemon↔web wire contract,
 *  defined once in src/shared and imported (type-only, so nothing bundles) rather than re-declared —
 *  the web mirror can no longer drift from what the daemon serves (the /auth/me User actually drifted
 *  once). `BrainMessage` is the web's name for the daemon's `BrainMessageView`; the memory/category/
 *  event/goal DTOs likewise keep their web-side names (`Memory`, `MemoryCategory`, `MemoryEvent`,
 *  `BrainGoal`) as aliases of the shared rows. */
import type {
  BrainMessageView, BrainMessageImage, BrainMessageFile, SlashCommandDef, AskQuestion, BrainStreamControl,
  BrainWorkMode, BrainPendingPlan,
  User, BrainLimits, BrainProviderCompatibility, RuntimeConfig as WireRuntimeConfig, RuntimeLimits, ToolDeferralOverrides, BrainUsage, MemoryRow, MemoryCategoryRow, MemoryEventRow, BrainGoalState,
  MemoryMaintenanceJob, MemoryMaintenanceState, MemoryRecategorizeMode,
  MemoryVitalityHistory,
  MemoryDreamingSettingsSnapshot, MemoryDreamingSettingsPatch,
  MemoryDreamingRefusalReason, MemoryDreamingRunView, MemoryDreamingChange, MemoryDreamingStatus,
  BrainContextBreakdown, BrainForkedSession, BrainRewindSuccess,
  BrainDebugPage, BrainDebugSessionPage, BrainDebugSessionItem, BrainDebugRequestItem, BrainDebugRequestDetail, BrainDebugSegmentManifestItem, BrainDebugSegmentPayload,
  BrainDebugRawPayload, BrainDebugLegacyTranscriptPage,
  ProjectView, ProjectMemberView,
} from '../../src/shared/wireContract.js';
export type { ProjectMemberView };
export type { MessageBlockView, MessageBlockTone } from '../../src/shared/wireContract.js';
// `BrainStreamControl` is only referenced by the snapshot frame below, so it is imported but not re-exported.
/** SlashCommandDef is served by GET /brain/commands; prompt macros send raw slash arguments for daemon expansion. */
export type { BrainMessageImage, BrainMessageFile, SlashCommandDef, AskQuestion, BrainWorkMode, BrainPendingPlan, User, BrainLimits, BrainProviderCompatibility, RuntimeLimits, BrainUsage, MemoryMaintenanceJob, MemoryMaintenanceState, MemoryRecategorizeMode };
export type {
  BrainContextBreakdown, BrainForkedSession, BrainRewindSuccess,
  BrainDebugPage, BrainDebugSessionPage, BrainDebugSessionItem, BrainDebugRequestItem, BrainDebugRequestDetail, BrainDebugSegmentManifestItem, BrainDebugSegmentPayload,
  BrainDebugRawPayload, BrainDebugLegacyTranscriptPage,
};
export type BrainMessage = BrainMessageView;
/** One stored memory as served by `GET /memory` — the daemon's `MemoryRow` plus its server-computed
 *  vitality (0–100), attached by the route. The web only displays it; it never recomputes it (the
 *  half-life table lives daemon-side). */
export type Memory = MemoryRow & { vitality: number };
export type MemoryCategory = MemoryCategoryRow;
export type MemoryEvent = MemoryEventRow;
/** A memory's vitality over time, rebuilt daemon-side from its recall log. The web draws it as-is —
 *  reconstructing it here would mean shipping the half-life table to the browser. */
export type { MemoryVitalityHistory };
/** The account's automatic memory maintenance (dreaming): its preference blob with the revision the
 *  PATCH is checked against, the runtime status the card reads, one run, and the before/after of what a
 *  run changed. Defined once in the wire contract, like every other daemon↔web DTO here. */
export type {
  MemoryDreamingSettingsSnapshot, MemoryDreamingSettingsPatch,
  MemoryDreamingRefusalReason, MemoryDreamingRunView, MemoryDreamingChange, MemoryDreamingStatus,
};
/** `GET /memory/dreaming/runs/:id`: the run itself plus what its audit events recorded. */
export interface MemoryDreamingRunDetail { run: MemoryDreamingRunView; changes: MemoryDreamingChange[] }
/** Durable autonomous-goal state, including the stored subgoal array. */
export type BrainGoal = BrainGoalState;

/** Memory auto-retention (`runtime.memoryRetention`), mirrored from `src/brain/memoryVitality.ts` — the
 *  daemon serves the block inside `runtime` and the web editor mirrors the shape here so the two stay in
 *  step (the wire contract deliberately does not import it, to avoid a cycle). `halfLifeByImportance`
 *  carries one positive half-life in days for each importance level 1..10; there is no "never" value. */
export interface MemoryRetentionConfig {
  enabled: boolean;
  graceDays: number;
  vitalityFloor: number;
  /** Automatic moves to the trash allowed per account in any rolling 24 hours. */
  dailyDeletionLimit: number;
  halfLifeByImportance: Record<number, number>;
}

/** The runtime block the daemon serves extends the wire shape with the retention group (see the daemon's
 *  `RuntimeConfigWithRetention` in src/store/configStore.ts). Optional like `brain.limits`: a daemon
 *  predating the feature serves the wire shape alone, and the editor seeds the defaults. */
export type RuntimeConfig = Omit<WireRuntimeConfig, 'toolDeferralOverrides' | 'hostedToolSearch' | 'wireApi' | 'providerRequestCaptureEnabled'> & {
  /** Optional while a web client may still receive a response from a daemon predating tool deferral overrides. */
  toolDeferralOverrides?: ToolDeferralOverrides;
  /** Optional while a web client may still receive a response from a daemon predating detailed request capture. */
  providerRequestCaptureEnabled?: boolean;
  /** Probe-owned server state; optional for compatibility and omitted from generic runtime PATCHes. */
  hostedToolSearch?: WireRuntimeConfig['hostedToolSearch'];
  memoryRetention?: MemoryRetentionConfig;
};

/** One backwards page of chat history (lazy-load). `nextBefore` is the cursor for the next older page —
 *  null once the oldest turn has been loaded, which is also when `hasMore` is false. */
export type BrainMessagePage = import('../../src/shared/wireContract').BrainMessagePage;

/** The chat stream's first frame (`?snapshot=1`): the newest page of durable history plus the current
 *  run's not-yet-durable tail, captured on one event-loop tick. Mirror of the daemon's `BrainStreamSnapshot`
 *  (src/brain/session/liveEventReplay.ts), narrowed to what the web reads — the tail is kept as bare tagged
 *  frames because it also carries out-of-band events the transcript fold has no case for. */
export type BrainStreamTailEvent = import('../../src/brain/transcript').TranscriptEvent;
export interface BrainStreamSnapshotFrame {
  history: BrainMessage[];
  events: BrainStreamTailEvent[];
  /** Accepted inert messages still pending durable history insertion; rendered after the replay tail. */
  conversationAppends?: BrainMessage[];
  /** The session actually tapped — differs from the requested one after a server-side rebind the dead
   *  stream never saw. */
  sessionId?: string;
  /** Authoritative identity of the tapped session, including a child drill-in. Same public/internal split
   *  as {@link BrainStatus}: `provider`/`providerLabel` are for display, `usageProvider` for the rail. */
  session?: BrainSessionSnapshot;
  /** Persisted display cards for reconnect and read-only drill-in hydration. */
  cards?: BrainCard[];
  processes?: ProcessInfo[];
  /** Durable owner-activity sequence captured with this snapshot. */
  activitySeq?: number;
  /** The daemon's authoritative control state at snapshot time. The tail is transient — cleared at settle,
   *  bounded, and terminal-less across an internal retry — so this, not the tail's shape, decides whether a
   *  turn is running and whether a question is parked. Both fields are explicit, so hydrating from this
   *  frame CLEARS what the daemon no longer has. The shape comes from the shared wire contract. */
  control: BrainStreamControl;
  /** The conversation's durable goal at snapshot time. It is independent of the transient run journal
   *  (cleared at settle), so this — explicit `null` included — is what lets a reconnecting client learn a
   *  goal it never saw, or clear one that ended while it was away. Absent means an older daemon. */
  goal?: BrainGoal | null;
  promptSuggestion?: string | null;
  /** The live journal dropped part of the unsettled run; durable history must be refetched once it settles. */
  truncated?: true;
  hasMore?: boolean;
  nextBefore?: string | null;
}

/** `AskQuestion` comes from the shared wire contract (re-exported at the top of this file) rather than
 *  being mirrored here. The `ask` SSE event carries `id` + `questions`; the client POSTs `answers` back to
 *  /brain/answer, and that answer shape is web-side only. */
export interface AskAnswer { header: string; selected: string[]; other?: string }

/** ctx.emitCard display card from the brain event contract, imported type-only so the browser
 *  bundle never resolves the daemon module. */
export type { BrainCard } from '../../src/shared/wireContract';

/** Daemon-projected conversation process. The status/stream boundary owns account and conversation
 * filtering. Rails omit foreground calls and exited jobs; output and kill retain their owner gates. */
export interface ProcessInfo { id: string; command: string; cwd: string; startedAt: string; sessionId: string | null; running: boolean; exitCode: number | null; completionMode?: 'job' | 'service' | 'foreground' }

/** The statusline plugin's display toggles (null = plugin disabled). */
export interface StatuslineConfig { showModel?: boolean; showContext?: boolean; showTokens?: boolean; showSpeed?: boolean; showCacheHit?: boolean; showCost?: boolean }
/** Where the conversation works: the live (or last stamped) directory and its git branch. Both null for
 *  a chat that never reported a directory — an ordinary web conversation has no client cwd. Optional for
 *  an older daemon. */
export type BrainProject = NonNullable<BrainSessionIdentity['project']>;
/** One viewer-scoped MCP observation; null means the plugin contributed no MCP section. */
export type McpServerStatus = NonNullable<import('../../src/plugins/api').PluginBrainStatus['mcp']>[number];
export type { ProjectExecutionRef } from '../../src/shared/wireContract';
export type { ApiTokenDay, ApiTokenLimitValues, ApiTokenListItem, ApiTokenStats, ApiTokenView } from '../../src/shared/wireContract';
import type { CliDeviceStats } from '../../src/shared/cliDevices';
export type { CliDeviceStats } from '../../src/shared/cliDevices';
export type CliDeviceDay = CliDeviceStats['days'][number];
/** A pending device-code sign-in, as the approval card reads it. `expiresAt` is epoch milliseconds. */
export interface CliDeviceCodeView { userCode: string; deviceName: string; kind: import('../../src/shared/cliDevices').CliLoginKind; expiresAt: number }
/** A one-time install command. `expiresAt` is epoch milliseconds; the ticket inside dies with it. */
export interface CliEnrollmentStatus { expiresAt: number; remainingMs: number }
export interface CliEnrollment extends CliEnrollmentStatus { id: string; command: string }
/** `provider` is the public config identity; `providerLabel` is its display name.
 *  `usageProvider` is the internal provider key for rate-limit reads, never a display name. */
export interface BrainStatus extends BrainSessionSnapshot, BrainStreamControl { projectRef?: import('../../src/shared/wireContract').ProjectExecutionRef; running: boolean; sessionId: string | null; usage: BrainUsage | null; statusline: StatuslineConfig | null; cards?: BrainCard[]; processes?: ProcessInfo[]; queued?: import('../../src/shared/wireContract').BrainQueueMemberItem[]; yolo?: boolean; project?: BrainProject; chatRailSections?: readonly BrainChatRailSection[]; mcp?: McpServerStatus[] | null }
export type { ProviderQuotaHistory, ProviderUsage, ProviderUsageStats, RoutedUsageKey, UsageWindow } from '../../src/shared/wireContract';
/** A running OAuth connect flow, as polled by the settings UI. */
export interface OAuthFlowState {
  id: string;
  provider: string;
  status: 'pending' | 'action-required' | 'success' | 'error';
  authUrl?: string;
  instructions?: string;
  userCode?: string;
  needsInput: boolean;
  error?: string;
}
export interface ConfigPatch {
  promptSuggestionModel?: PromptSuggestionModel;
  voice?: Partial<VoiceConfig>;
  allowedSkins?: string[];
  security?: { tokenTtlDays?: number; trustProxy?: boolean };
  sessionRetention?: { enabled?: boolean; days?: number };
  autoUpdate?: boolean;
  problemReporting?: { enabled: boolean };
  problemReportingConfirmation?: true;
  webPushContact?: string;
  /** Wholesale brain provider list. A connection that carries no `apiKey` keeps the stored one, so the UI
   *  can round-trip an account without ever seeing or resending its secret; an explicit null clears it. */
  brain?: { providers?: (Omit<BrainProvider, 'connections'> & { connections?: (Omit<BrainProviderConnectionView, 'apiKeySet'> & { apiKey?: string | null })[] })[]; defaultModel?: { providerId: string; model: string }; maxSteps?: number; modelOverrides?: ModelOverrides; limits?: Partial<BrainLimits>; forkParentContext?: boolean };
  /** Runtime knobs merged per-field by the daemon, like the brain limits above. */
  runtime?: { limits?: Partial<RuntimeLimits>; toolDeferralEnabled?: boolean; toolDeferralOverrides?: ToolDeferralOverrides; subagentRunnerEnabled?: boolean; subagentRunnerPoolMax?: number | null; providerRequestCaptureEnabled?: boolean; memoryRetention?: Partial<MemoryRetentionConfig> };
  /** Dashboard personalization block, merged per-field by the daemon. */
  dashboard?: { recapEnabled?: boolean; digestEnabled?: boolean; greetingEnabled?: boolean; pillsEnabled?: boolean; continueEnabled?: boolean; digestPerDay?: number; digestVariants?: number; digest?: { providerId?: string; model?: string } };
}
export interface ProfilePatch { name?: string; email?: string }

/** The stored JSON settings an account can have that exist but cannot be read (see the cli-settings API). */
export type UnreadableCliSettingKey = 'autoCompactAtByModel' | 'projectModelPreferences' | 'fastModels';
/** Per-user CLI/brain settings surfaced in Account. An empty model uses the configured brain default.
 *  platformLinks holds canonical stored values by platform id; patches include only edited members.
 *  availableLinks lists current identity registrations. Every GET, save and conflict snapshot carries both. */
export interface CliSettings { platformLinks: Record<string, string>; model: string; modelProvider: string; visionModel: string; visionModelProvider: string; compactModel: string; compactModelProvider: string; thinkingLevel: string; autoCompact: boolean; autoCompactAt: number; autoCompactAtByModel: Record<string, number>; projectModelPreferences?: Record<string, { provider: string; model: string }>; advisorStyle: string; userInstructions: string; autoRecall: boolean; autoLiveRecall: boolean; autoSave: boolean; autoLearnSkills: boolean; promptSuggestions: boolean; skillLearningAvailable?: boolean; skillReview?: SkillReviewStatusView; fastModels: string[]; unreadable?: UnreadableCliSettingKey[]; resetUnreadable?: UnreadableCliSettingKey[]; serverDefaultRoute?: { provider: string; providerLabel: string; model: string }; effectiveChatRoute?: { provider: string; model: string } | null; availableLinks: string[]; revision?: number; pending?: boolean }
export interface UserPluginConfigDetail {
  name: string;
  /** Short English name for this plugin's per-account settings entry (manifest `userConfigLabel`), what
   *  the Account rail calls it. Absent for a plugin that declares none — the rail then falls back to the
   *  plugin name, never to `description`, which is a sentence and belongs in the panel. */
  label?: string;
  description?: string;
  userConfigSchema: PluginConfigField[];
  config: Record<string, unknown>;
  secretsSet: string[];
  /** As on `PluginDetail`: stored secrets the daemon cannot decrypt, present only when one exists. */
  secretsUnreadable?: string[];
  revision: number;
  /** Where these values are EDITED (manifest `userConfigPlacement`). `pluginPage` means the plugin's own
   *  page presents them, so the Account rail must not offer a second form for the same setting. Absent on
   *  an older daemon, which is the `account` default. */
  placement?: 'account' | 'pluginPage';
  i18n?: Record<string, PluginI18n>;
}

export type { PermissionAction, PermissionSettingsSnapshot as PermissionSettings, TerminalPalette } from '../../src/shared/wireContract';

/** Account navigation preferences, persisted as one revisioned document. Menu ids are resolved by
 *  `lib/navLayout.ts`; conversation ids use the shared pin ordering rule. */
export interface NavLayout {
  /** Ids the user hid. Hidden worlds stay reachable by URL and through the command palette. */
  hidden: string[];
  /** Preferred order, by id. Entries it does not mention keep their registry order, behind the rest. */
  order: string[];
  /** Pinned conversation ids, top first; unlisted pins appear before these. */
  pinOrder: string[];
  /** Absent until the daemon ships account widths; null follows the skin default. */
  sidebarWidth?: number | null;
  revision?: number;
}

export type { TerminalFontFamily, TerminalCursorStyle, TerminalThemeMode, TerminalSettings, TerminalSettingsSnapshot } from '../../src/shared/wireContract';

/** Installed platform presentation from GET /plugins/platforms, independent of browser bundles. */
export interface PlatformMetadata {
  platform: string;
  plugin: string;
  label: string;
  available: boolean;
  identity?: { title: string; help: string; placeholder: string };
}

/** One row of GET /plugins/ui — an enabled plugin's browser UI: menu metadata (renderable before any
 *  bundle JS runs) plus the immutable content-hash bundle URL. */
export interface PluginUiListing {
  name: string;
  /** Present only in an exact user-page query; never part of the shell listing. */
  page?: string;
  /** Exact user-page routes reserved outside the main bundle. A routing hint, never a mount grant:
   *  the matching targeted listing and scoped asset requests must still authorize the page. */
  userPages?: string[];
  url: string;
  /** Immutable content-hash URL of the plugin's OWN stylesheet, when it ships one. The app is
   *  distributed PREBUILT, so its CSS carries only the utilities the host itself uses — a plugin that
   *  needs any other one has to bring it. Absent for a plugin (or a daemon) that ships none. */
  cssUrl?: string;
  apiVersion: number;
  /** How many of the plugin's things are waiting for THIS account, resolved server-side by the plugin's
   *  own `registerSurfaceFact('navBadge', fn)` probe. Always positive; absent for no badge (and from any older daemon). */
  badge?: number;
  /** Name of the plugin's world in the main navigation (manifest `web.label`, localized). Absent = the
   *  world borrows its first page's name. */
  label?: string;
  /** The measure the shell frames this plugin's pages at (manifest `web.layout`). 'workbench' asks for
   *  the wide application frame; absent — which is also what an older daemon sends — means the ordinary
   *  page measure every core route is read at. */
  layout?: 'document' | 'workbench';
  /** `false` when the plugin's pages close without the instance wordmark (manifest `web.wordmark`);
   *  absent — also what an older daemon sends — means the word closes them like every other page. */
  wordmark?: false;
  /** Host presentation of /p/<plugin>. 'overlay' uses the same PageOverlay as Settings and Account. */
  presentation?: 'page' | 'overlay';
  nav: { label: string; icon?: string; route?: string }[];
  /** Per-account plugin panels. Optional for compatibility with an older daemon listing.
   *  `placement` picks where the panel hangs: 'section' (the default, and what a daemon too old to send
   *  the field means) gives it its own entry in the Account section rail; 'linkedAccount' mounts it as a
   *  row of the Linked accounts drawer instead. */
  account?: { id: string; label: string; icon?: string; placement?: 'section' | 'linkedAccount'; order?: number }[];
  /** Administrator-only panels contributed to a selected User. Optional for older daemons. */
  user?: { id: string; label: string; icon?: string }[];
  /** Contextual panels contributed to a selected Project. Optional for older daemons. A panel with
   *  `placement: 'overview'` is appended to the Overview tab instead of claiming a tab of its own;
   *  `'overviewTop'` is drawn in Overview too, but directly under its title and description, before every group. */
  project?: { id: string; label: string; icon?: string; placement?: 'tab' | 'overview' | 'overviewTop' }[];
  /** Slots declared by the manifest, available before the bundle executes. */
  slots: string[];
  /** Operation dock policy accompanies its slot declaration. */
  operationDock?: { desktopOnly: boolean };
  /** Placement offers the section as a page or inside the plugin detail. Absent means page. */
  settings: { id: string; label: string; icon?: string; placement?: 'page' | 'pluginDetail' }[];
  /** Localized flat view strings for the bundle (manifest `web.strings` merged with the locale's
   *  i18n overrides server-side). Optional so an older daemon's listing still parses. */
  strings?: Record<string, string>;
}

/** Update clients use the coordinator's exact wire types, with no independently maintained mirror. */
export type { QueuedUpdateResponse, SystemUpdateStatus, UpdateConsentPlan, UpdateState,
  PluginOutcome as PluginUpdateOutcome } from '../../src/shared/wireContract';

/** One installed daemon plugin as listed by admin GET /plugins. */
export interface PluginInfo {
  name: string;
  /** What the plugin register and detail CALL this plugin (manifest `label`). `name` stays the
   *  technical id behind config, controls, mounts and grants. Absent → the id is the title. */
  label?: string;
  /** Manifest identity glyph from the closed Lucide vocabulary; absent means the neutral puzzle. */
  glyph?: string;
  version: string;
  description: string;
  provides: { tools?: string[]; skills?: string[]; hooks?: string[]; platforms?: string[]; destinations?: string[]; controls?: string[] };
  dependencies?: PluginDependency[];
  source: 'bundled' | 'user';
  enabled: boolean;
  /** A soft-removed bundled plugin: hidden from the installed list, not loaded, restorable from the
   *  Available tab. Only ever true for `source: 'bundled'` (user plugins are uninstalled outright). */
  removed?: boolean;
  degraded?: { installedVersion: string; reason: string };
  /** The plugin is handed out PER USER: non-admins reach it only once an admin grants it to them
   *  (`User.granted_plugins`). Drives the grant picker in the users panel. */
  userGrantable?: boolean;
  /** Per-locale manifest translations (from the plugin's `i18n/<lang>.json`). English lives in the
   *  manifest itself and is the fallback; a locale entry overrides `description` + per-field label/hint. */
  i18n?: Record<string, PluginI18n>;
  /** Derived from the plugin's log ring buffer: `error` when a recent error entry exists, else `ok`.
   *  Defaults to `ok` when the daemon has no log tap. */
  health?: 'ok' | 'error';  /** Instance credential fields the manifest declares and the vault's corrupt ones (Settings → Access). */
  credentials?: PluginCredentialsSummary;
}

/** Localized overrides for a plugin's manifest strings, keyed by config-field key. */
interface PluginI18n {
  description?: string;
  /** Localized display name for the plugin itself (manifest `label`). */
  label?: string;
  /** Localized short name for the plugin's per-account settings entry (manifest `userConfigLabel`). */
  userConfigLabel?: string;
  fields?: Record<string, { label?: string; hint?: string; help?: string; options?: Record<string, string> }>;
}

/** One row of the plugin marketplace catalog (GET /plugins/marketplace): a curated-registry entry plus
 *  its on-disk status. `available` — installable. `installed` — a user plugin, up to date. `updateAvailable`
 *  — a user plugin with a newer version in the registry. `bundled` — the name is a built-in, so it's never
 *  offered for install/update. */
export interface MarketplaceEntry {
  name: string;
  version: string;
  description: string;
  category?: string;
  author?: string;
  homepage?: string;
  label?: string;
  glyph?: string;
  requiresControls?: string[];
  dependencies?: PluginDependency[];
  provides?: { tools?: number; skills?: number; platforms?: number; controls?: string[] };
  status: 'available' | 'installed' | 'updateAvailable' | 'bundled';
  installedVersion?: string;
  degraded?: { installedVersion: string; reason: string };
}

/** GET /plugins/marketplace. `registryError` is set when the registry couldn't be reached/refreshed, so
 *  the UI can distinguish "unavailable" from a genuinely empty catalog. */
export interface Marketplace {
  plugins: MarketplaceEntry[];
  registryError?: string;
}

/** One declared plugin config field (drives the per-plugin settings form). The validated manifest type,
 *  imported type-only so the browser bundle never resolves the daemon module. */
export type { PluginConfigField } from '../../src/plugins/manifest';

/** One platform role policy: admission, platform-admin status, and room-specific instructions. */
export interface RolePolicy { roleId: string; name: string; prompt: string; admin?: boolean }

import type { PluginCapabilities } from '../../src/plugins/capabilities';

/** A durable plugin change activates after a quiet window from the last request, then a pause.
 *  Absent when the deployment cannot restart itself. */
export interface PluginRestartPlan {
  quietMs: number;
}

/** The accepted plugin-config write and its activation posture. */
export interface PluginConfigSaveResponse {
  ok: true;
  pending?: boolean;
  restart?: PluginRestartPlan;
  revision?: number;
  config?: Record<string, unknown>;
  secretsSet?: string[];
  secretsUnreadable?: string[];
}

/** GET /plugins/:name, including its config editor snapshot and persistent-data summary. */
export interface PluginDetail extends PluginInfo {
  configSchema: PluginConfigField[];
  config: Record<string, unknown>;
  secretsSet: string[];
  /** Stored secrets the daemon cannot decrypt (also in `secretsSet`); present only when one exists. */
  secretsUnreadable?: string[];
  /** Revision of the shared config snapshot used by conditional plugin writes. */
  revision?: number;
  /** Summary of the plugin's persistent data directory (`pluginDataRoot/<name>`). `path` is `''` and
   *  `exists:false` when the data root is unset or the name is unsafe; `files`/`bytes` are recursive totals. */
  data: import('../../src/shared/wireContract').PluginDataSummary;
  /** The plugin's declared manifest capabilities, or `{}` (deny-all) when the manifest omits them. */
  capabilities?: PluginCapabilities;
  /** Admin-only authority coverage from the existing consent store. Absent for a degraded manifest;
   *  persisted grants carry no per-grant migration/manual-approval provenance. */
  authorityConsents?: { required: string[]; granted: string[]; missing: string[] };
}

/** The contributions the LOADED plugin registry actually reports, each tagged with its owning plugin.
 *  Served both by GET /plugins/:name/contributions — filtered to entries OWNED by the requested plugin, so
 *  every `plugin` field equals that name, powering the Tools and Hooks detail sections — and unfiltered by
 *  GET /plugins/runtime, which is what lets one web control ask whether ANY plugin contributes a given
 *  kind (the mid-turn reminder row is offered only when `stepContexts` is non-empty). */
export interface PluginContributions {
  tools: { name: string; plugin: string }[];
  skills: { name: string; plugin: string }[];
  platforms: { name: string; plugin: string }[];
  promptFragments: { plugin: string }[];
  turnContexts: { plugin: string }[];
  /** Mid-turn context providers (`ctx.registerText('step-context', fn)`) — unnamed, only the owner is known. */
  stepContexts: { plugin: string }[];
  hooks: { name: string; plugin: string }[];
}

/** One entry of a plugin's bounded log ring buffer (scope-stripped, oldest-first). */
interface PluginLogEntry {
  ts: number;
  level: 'debug' | 'info' | 'warn' | 'error';
  message: string;
}

/** GET /plugins/:name/logs — the tail of the plugin's log ring buffer. */
export interface PluginLogs {
  entries: PluginLogEntry[];
}

export type { LogFileList, LogFileContent } from '../../src/shared/wireContract';

/** One recorded run of a plugin hook (the shared HookAuditBuffer). `outcome`: `ok` = a context patch was
 *  accepted (`changed === 'turnContext'`); `rejected` = the capability gate denied the patch (deny-by-default,
 *  no `changed`); `threw`/`timeout` = fail-open, the hook produced no patch (no `changed`). `ts` is epoch ms. */
export interface PluginHookExecution {
  ts: number;
  plugin: string;
  hook: string;
  durationMs: number;
  outcome: 'ok' | 'threw' | 'timeout' | 'rejected';
  changed?: string;
}

/** GET /plugins/:name/hook-executions — the plugin's hook-run audit, NEWEST-FIRST. Empty when the
 *  hook-audit buffer isn't wired. */
export interface PluginHookExecutions {
  entries: PluginHookExecution[];
}

/** One recurring job shown as a navigation row under the conversation it is organized under — the
 *  response of GET /brain/conversation-links. Organization only: opening the link opens the job's existing
 *  editor and changes nothing about when it runs, which model it uses or where its result is delivered.
 *
 *  `href` is generated by the daemon from the job id, never contributed by the plugin, so a link row can
 *  only ever point at the cron editor on this same origin. `scope` distinguishes a personal job from an
 *  instance one; the daemon has already applied job visibility, so every row here is one the caller may
 *  see. Prompts, schedules and results are deliberately absent — navigation never shows them. */
export type { ConversationJobLink, ConversationLinksResponse, ConversationSubagentNode } from '../../src/shared/wireContract';

/** One admin-selectable proactive-notification target from an enabled platform plugin. `value` is the
 * opaque persisted routing token; callers display metadata but never construct or parse it. */
export interface NotificationDestinationOption {
  value: string;
  id: string;
  platform: string;
  kind: 'channel' | 'thread' | 'chat' | 'person';
  label: string;
  group?: string;
  subtitle?: string;
}

/** One admin-selectable tool from the live built-in + enabled-plugin registry catalog. */
export interface ToolCatalogOption {
  name: string;
  label: string;
  icon: string | null;
  plugin: string | null;
  group: 'memory' | 'image' | 'core' | 'plugin';
}
/** One typed sub-agent of the subagent plugin (GET /plugins/agents/list). Built-in explore/plan ship
 *  with the install and are read-only; user agents are one `.md` each and can be edited or deleted.
 *  `tools` is the frontmatter spec: a preset keyword (`read-only`/`all`/`inherit`) or an explicit tool
 *  allow-list. `body` (the system prompt) is present only for user agents, so the editor can prefill.
 *  `model` is the definition's own fixed model, present only when it names one. */
export interface PluginSubagent { name: string; description: string; tools: 'read-only' | 'all' | 'inherit' | string[]; source: 'builtin' | 'user'; canDelete: boolean; body?: string; model?: string }
// The shared login union distinguishes pending proof from a session; the BFF removes credentials.
export type { AuthenticatedSession, BrowserLoginResult, AdminSecurityProof, LoginSessionView, AccountSecurityStatus, TotpEnrollment, LoginSessionsPage } from '../../src/shared/wireContract';
/** A person on the content-free activity presence line. */
export interface PresenceEntry {
  userId: number;
  label: string;
  username: string;
  avatar?: string;
  working: boolean;
  lastTs: string;
}

/** One person on the dashboard's team pulse. It carries what somebody is working on and what they spent
 *  today — see the '/activity/pulse' route comment for why that is a deliberate product decision rather
 *  than a leak. */
export interface PulsePerson {
  userId: number;
  label: string;
  username: string;
  avatar?: string;
  /** The daemon's LIVE view of a running turn, never inferred from history. */
  working: boolean;
  /** Whether this person left any trace TODAY. The list also carries people who only worked earlier in
   *  the ring's month, so "active today" cannot be read from the list's length. */
  activeToday: boolean;
  /** The open conversation's title while they are working, else ''. */
  title: string;
  lastTs: string;
  turns: number;
  tokens: number;
  /** null when no contributing turn carried a price — distinct from 0, which means "priced at zero". */
  cost: number | null;
  /** Share of context served from a warm cache prefix, 0–100. Null when nothing ran today: a person with
   *  no turns has no ratio, and "0 %" would read as a cold cache rather than an absent measurement. */
  cacheHitPct: number | null;
  /** Memories this person's turns recalled today. */
  memoryHits: number;
  /** Where today's turns came from: 'web', 'cli', 'internal', 'discord', 'cron', … */
  surfaces: string[];
  /** Turns per hour, exactly 24 entries, indexed by UTC hour — the same basis `memoryByHour` uses, so
   *  the chart shifts both series into local time with one offset. */
  hoursToday: number[];
  /** The same person over the trailing month — what the ring divides and what its hover card reports.
   *  Kept beside the daily figures rather than replacing them: the gauges above the ring still mean
   *  today, and one shape carrying two windows would make every reader check which one they are seeing. */
  month: {
    turns: number;
    tokens: number;
    cost: number | null;
    cacheHitPct: number | null;
    memoryHits: number;
    surfaces: string[];
  };
}

/** The whole team pulse tile in one response. */
export interface PulseResponse {
  today: string;
  /** The rings' window: `from` is its first day (inclusive, UTC), `days` its length. */
  month: {
    from: string;
    days: number;
    tokens: number;
    cost: number | null;
    /** The same month folded by WHERE the work came from. Every web turn is keyed by caller IP in the
     *  rollup, so those collapse into one 'web' entry rather than an address per slice. */
    surfaces: { surface: string; turns: number; tokens: number; cost: number | null }[];
    /** Where the tokens went. Warm cache reads bill at a fraction of fresh input, so this split is what
     *  says whether a big month was also an expensive one. */
    context: { cacheRead: number; input: number; cacheWrite: number; output: number };
  };
  people: PulsePerson[];
  totals: {
    turns: number;
    tokens: number;
    cost: number | null;
    activePeople: number;
    /** Delegated sessions streaming right now — sub-agents, not people. */
    runningAgents: number;
    memoryHits: number;
    /** Instance-wide cache hit share, weighted by context rather than averaged across people. */
    cacheHitPct: number | null;
  };
  /** Yesterday's totals, so each headline can carry a delta without a second request. */
  yesterday: { people: number; turns: number; tokens: number };
  /** Instance-wide memory recalls per UTC hour, 24 entries. */
  memoryByHour: number[];
  /** False when the usage rollup is unavailable, so the tile says so instead of a confident $0. */
  spendAvailable: boolean;
}

export type { ActivityFeedEvent } from '../../src/shared/wireContract';
export type { ProjectDeletion, ProjectDeletionPreview } from '../../src/shared/wireContract';
export type Project = ProjectView;
/** A stable key for one execution target: what a radio group addresses, and how two targets are compared.
 *  An administrator's nameless host has no project behind it, hence the empty tail. */
export function executionRefKey(ref: ProjectExecutionRef): string {
  return `${ref.kind}:${ref.projectId ?? ''}`;
}
export type { ProjectSummary } from '../../src/shared/wireContract';
interface GitStatus { branch: string; head: string; upstream: string | null; ahead: number; behind: number; dirty: number; clean: boolean }
interface GitRemote { name: string; fetchUrl: string; pushUrl: string }
interface GitBranch { name: string; current: boolean }
interface GitCommit { hash: string; subject: string; author: string; relative: string }
export interface ProjectGit { isRepo: boolean; status: GitStatus | null; remotes: GitRemote[]; branches: GitBranch[]; commits: GitCommit[] }
/** One anchored page of a project's commit log. `anchor` is the commit the page was read from: the
 *  page that had none anchored at HEAD and answered its own, so later pages stay still while new
 *  commits land. `hasMore` says whether the log continues past the page. */
export interface ProjectCommitPage { commits: ProjectGit['commits']; hasMore: boolean; anchor: string | null }

/** The lifecycle a managed project's environment is in (mirrors the daemon's `ProjectEnvironment`
 *  state). Only `running` can answer a file route; every other state means the guest filesystem is not
 *  there to read. */
type ProjectEnvironmentState = 'unprovisioned' | 'starting' | 'running' | 'stopped' | 'failed' | 'deleting' | 'deleted';
/** The bounded slice of the Sandbox plugin's read-only environment overview that core screens use. The
 *  plugin owns the full projection; core reads only the state, and only to decide what it may ask for
 *  next. */
export interface ProjectEnvironmentOverview { environment: { state: ProjectEnvironmentState } }

/** A shallow directory listing for the new-project path picker (server-side filesystem browse). */
export interface DirListing { path: string; parent: string | null; entries: { name: string; path: string }[] }

/** Elowen's own version + update posture for the System settings panel. `latest` is null when the npm
 *  registry can't be reached; `updateAvailable` is then false. */
export interface SystemInfo {
  version: string;
  latest: string | null;
  updateAvailable: boolean;
  autoUpdate: boolean;
  diagnostics: {
    cpuPercent: number;
    memoryUsedBytes: number;
    memoryTotalBytes: number;
    uptimeSeconds: number;
    diskUsedBytes: number;
    diskTotalBytes: number;
  };
}

/** One first-run readiness row from GET /system/readiness — a subsystem the onboarding UI reports on
 *  (chat, plugin secrets, memory, platforms, plugins, webhooks). `ok=false` on `chat` means no provider resolves, so
 *  the agent cannot answer. Admin-only endpoint. */
export interface ReadinessCheck {
  id: string;
  label: string;
  ok: boolean;
  detail: string;
  hint?: string;
  /** Set only on rows a plugin contributed, so its own settings screen can show its status from the
   *  same check the onboarding report reads — one health path, two places it is rendered. */
  plugin?: string;
  /** Values to transcribe somewhere this instance cannot reach (a registrar's DNS panel). Rendered as
   *  labelled, copyable fields rather than prose. */
  fix?: { label: string; value: string }[];
}
export interface SystemReadiness { checks: ReadinessCheck[] }

// One stored memory — the daemon's `MemoryRow`, shared via the wire contract (re-exported at the top
// of this file). Per-user and private — every route derives identity from the session, never a
// body/param id. `status` is closed to what the daemon's API schema enums.

// One user-defined (or built-in) memory category (the daemon's `MemoryCategoryRow`, shared via the
// wire contract). Per-user. `is_builtin` is 0/1 and `color` is a hex/token string used by the UI badge.

/** Body for POST /memory/categories — only `name` is required (409 on duplicate name). */
export interface MemoryCategoryCreate { name: string; description?: string; color?: string; icon?: string; projectId?: number | null }
/** Any subset of the mutable fields for PATCH /memory/categories/:cid (409 on duplicate name). */
export interface MemoryCategoryPatch { name?: string; description?: string; color?: string; icon?: string; projectId?: number | null }

/** Workspace-level categorization provider settings (GET /memory/categorization). `configured` reflects
 *  whether provider/model/baseUrl are complete enough to classify. */
export interface CategorizationSettings {
  providerId: string;
  model: string;
  baseUrl: string;
  configured: boolean;
}
/** Patch for PUT /memory/categorization (admin-gated). */
export interface CategorizationSettingsPatch {
  providerId?: string;
  model?: string;
  baseUrl?: string;
}

// One entry of a memory's audit trail (the daemon's `MemoryEventRow`, shared via the wire contract).
// `memory_id` is null for events whose memory was hard-removed; `before_json`/`after_json` are raw JSON
// snapshots.

/** Body for POST /memory — only `body` is required. */
export interface MemoryCreate { body: string; kind?: string; importance?: number; confidence?: number }
/** Any subset of the mutable fields for PATCH /memory/:id. Category assignment is NOT here — it's a
 *  separate audited write via PUT /memory/:id/category (elowenClient.setMemoryCategory). */
export interface MemoryPatch { body?: string; kind?: string; importance?: number; confidence?: number; status?: 'active' | 'archived' | 'deleted' }
/** Query filters for GET /memory. `categoryId` present-and-null/empty lists uncategorized,
 *  a number lists that category, absent (key omitted) lists all. */
export interface MemoryFilters { status?: string; kind?: string; limit?: number; offset?: number; categoryId?: number | null }

/** Workspace-level embedding provider settings (GET /memory/embedding). `configured` reflects whether the
 *  provider/model/baseUrl are complete enough to embed. */
export interface EmbeddingSettings {
  providerId: string;
  model: string;
  baseUrl: string;
  dimensions: number | null;
  configured: boolean;
}

/** Patch for PUT /memory/embedding (admin-gated). */
export interface EmbeddingSettingsPatch {
  providerId?: string;
  model?: string;
  baseUrl?: string;
  dimensions?: number | null;
}

export type { ModelUsage, DayUsage, UserUsageResetResult } from '../../src/shared/wireContract';

/** `allowed` = in the account grant; `disabled` = absent from it; `unavailable` = the tool's
 * plugin is ungranted independently of its individual tool grant. */
type UserToolState = 'allowed' | 'disabled' | 'unavailable';

/** One tool on the users-panel access overview. The daemon supplies its declared emblem in `icon`;
 *  null leaves the client to render a core-owned mark or the neutral wrench.
 *  `plugin` is the owning plugin id, or null for built-ins. `toggleable` is true when the account
 *  grant can change this tool independently of plugin access and family-pattern grants. */
export interface UserToolPill {
  name: string;
  label: string;
  icon: string | null;
  plugin: string | null;
  group: 'memory' | 'image' | 'core' | 'plugin';
  state: UserToolState;
  toggleable: boolean;
}

/** Compact per-user overview stats for the users panel. `topModel` is the model used in the most brain
 *  sessions over the whole history, or null when the user has no sessions with a recorded model. */
export interface UserStats {
  memoryCount: number;
  sessionCount: number;
  topModel: string | null;
}

/** Whose spend GET /usage/by-model and /usage/by-day report: the caller's own, or (admin only) every account's. */
export type UsageScope = 'personal' | 'instance';

export type { UsageOriginGroup, UsageByOriginResult } from '../../src/shared/wireContract';

/** Severity of a bell alert (mirror of `AlertSeverity` in src/store/alertStore.ts). Producers are
 *  trusted code; the daemon validates on raise, so the web reads the value as-is. */
export type AlertSeverity = 'info' | 'warning' | 'critical';
/** One durable bell-alert row as served by `GET /alerts` (the daemon's `alertView` projection in
 *  src/alerts/alertService.ts). The daemon has already localized `title`, `subtitle` ('' when absent),
 *  `body` and every fact for the reader, so the bell only lays them out. `url` is an app path (the daemon
 *  rejects anything not starting with a single `/`). `readAt` null = unacknowledged. `actor` is the
 *  current profile of the account whose action raised the alert (an invitation's inviter), null when the
 *  alert names none or that account is gone; the bell draws its avatar instead of the severity icon. */
export interface Alert {
  id: number; source: string; key: string; severity: AlertSeverity; actor: Profile | null;
  title: string; subtitle: string; body: string; facts: { label: string; value: string }[];
  url: string; projectId: number | null; data: unknown; createdAt: string; updatedAt: string; readAt: string | null;
}
/** `GET /alerts`: the caller's open rows (newest movement first, bounded) plus the badge count —
 *  live rows without a `readAt`. */
export interface AlertsResponse { alerts: Alert[]; unread: number }
/** Muted kinds reach neither the bell nor subscribed devices. Missing kinds are enabled. */
export interface AlertSettings { muted: Record<string, string[]>; revision: number }
export interface AlertKind { source: string; kind: string; label: string }
export interface AlertKindGroup { id: string; label: string; glyph: string; kinds: AlertKind[] }
export interface AlertKindsResponse { groups: AlertKindGroup[] }

// --- Skins ----------------------------------------------------------------------------------------
// Catalogue and admin views share the type-only wire contract; browser resolution re-exports it.

/** `GET /skins` — the admin surface: the catalogue the instance serves, the folders it refused with the
 *  reason, and what a new skin may be built on. Admin only: a refusal is a diagnostic, not something a
 *  document renders. */
export type { SkinAdminView } from '../../src/shared/wireContract';

/** `POST /skins` — a new customer skin. The daemon validates every field (and the whole token sheet)
 *  before it writes anything, and answers a refusal with the reason. */
export interface SkinCreateRequest {
  id: string;
  displayName: string;
  base: string;
  colorScheme?: import('./skins').SkinColorScheme;
  tokensCss: string;
}
